import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAdmin } from '../../contexts/AdminContext';

const ReviewSection: React.FC = () => {
  const { reviews, products } = useAdmin();

  const maskEmail = (email: string) => {
    if (!email || !email.includes('@')) return email;
    const [localPart, domain] = email.split('@');
    if (localPart.length <= 3) {
      return `${localPart.charAt(0)}***@${domain}`;
    }
    return `${localPart.slice(0, 3)}***@${domain}`;
  };

  const approvedReviews = useMemo(() => {
    return reviews.filter(review => (review.status || (review.is_verified ? "approved" : "pending")) === "approved");
  }, [reviews]);

  const recentReviews = useMemo(() => {
    const sortedReviews = approvedReviews
      .sort((a, b) => new Date(b.createdAt || b.created_at || new Date()).getTime() - new Date(a.createdAt || a.created_at || new Date()).getTime())
      .slice(0, 3);

    return sortedReviews.map(review => {
      const product = products.find(p => p.id === review.productId);
      const createdDate = review.createdAt || review.created_at || new Date().toISOString();

      return {
        id: review.id,
        productName: product?.name || review.productName || 'Unknown Product',
        productImage: product?.image || '/images/products/default.png',
        productId: product?.id || 1,
        userName: maskEmail(review.userEmail || review.userName || 'Anonymous'),
        rating: review.rating,
        date: new Date(createdDate).toLocaleDateString('en-US', {
          year: 'numeric', month: 'short', day: 'numeric'
        }),
        content: review.content,
        images: review.images || [],
      };
    });
  }, [approvedReviews, products]);

  const avgRating = approvedReviews.length > 0
    ? (approvedReviews.reduce((sum, r) => sum + r.rating, 0) / approvedReviews.length).toFixed(1)
    : '0.0';

  const satisfaction = approvedReviews.length > 0
    ? Math.round((approvedReviews.filter(r => r.rating >= 4).length / approvedReviews.length) * 100)
    : 0;

  return (
    <section className="py-20 lg:py-28 bg-forest-900 text-cream-100 relative overflow-hidden bg-linen">
      {/* Subtle background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[50%] h-[50%] bg-forest-700/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[40%] h-[40%] bg-forest-800/20 rounded-full blur-3xl" />
      </div>

      <div className="relative section-container">
        {/* Header */}
        <div className="flex items-end justify-between mb-16 lg:mb-20">
          <div>
            <h2 className="font-display text-display-sm lg:text-display-md text-cream-100 tracking-tight">
              What our customers say
            </h2>
            <p className="text-body text-cream-400 mt-3">Real reviews from verified buyers</p>
          </div>
          <Link
            to="/review"
            className="hidden sm:inline-flex items-center gap-2 text-[14px] font-medium text-cream-400 hover:text-gold-400 transition-colors duration-300 group"
          >
            All reviews
            <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
            </svg>
          </Link>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-10 sm:gap-16 mb-16 pb-16 border-b border-cream-100/10">
          <div>
            <div className="font-display text-display-md text-cream-100 tracking-tight">{avgRating}</div>
            <div className="flex items-center gap-1 mt-2">
              {[...Array(5)].map((_, i) => (
                <svg key={i} className={`w-4 h-4 ${i < Math.round(Number(avgRating)) ? 'text-gold-400' : 'text-forest-700'}`} fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
            </div>
            <p className="text-micro text-cream-500 mt-1">Average rating</p>
          </div>
          <div className="w-px h-16 bg-cream-100/10" />
          <div>
            <div className="font-display text-display-md text-cream-100 tracking-tight">{approvedReviews.length}</div>
            <p className="text-micro text-cream-500 mt-1">Total reviews</p>
          </div>
          <div className="w-px h-16 bg-cream-100/10 hidden sm:block" />
          <div className="hidden sm:block">
            <div className="font-display text-display-md text-cream-100 tracking-tight">{satisfaction}%</div>
            <p className="text-micro text-cream-500 mt-1">Satisfaction</p>
          </div>
        </div>

        {/* Review Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6 mb-14">
          {recentReviews.map((review) => (
            <div key={review.id} className="bg-cream-100/5 backdrop-blur-sm border border-cream-100/10 rounded-xl p-7 lg:p-8 transition-all duration-500 ease-spring hover:bg-cream-100/10 hover:border-cream-100/20">
              {/* Stars */}
              <div className="flex items-center gap-1 mb-5">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className={`w-4 h-4 ${i < Math.floor(review.rating) ? 'text-gold-400' : 'text-forest-700'}`} fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>

              {/* Content */}
              <p className="text-body text-cream-300 leading-relaxed line-clamp-4 mb-8">
                "{review.content}"
              </p>

              {/* Footer */}
              <div className="flex items-center justify-between pt-5 border-t border-cream-100/10">
                <div>
                  <span className="text-body-sm font-medium text-cream-300 block">{review.userName}</span>
                  <span className="text-micro text-cream-500">{review.date}</span>
                </div>
                <Link
                  to={`/product/${review.productId}`}
                  className="text-micro text-cream-500 hover:text-gold-400 transition-colors line-clamp-1 max-w-[120px] text-right"
                >
                  {review.productName}
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-5">
          <Link to="/review/write" className="btn-accent font-semibold">
            Write a Review
          </Link>
          <div className="text-caption text-cream-500">
            Photo review <span className="font-semibold text-cream-300">$10 credit</span> · Text review <span className="font-semibold text-cream-300">$5 credit</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReviewSection;
